/**
 * THE generator form — intent-first zones (spec §3, built 2026-07-16).
 * One panel, progressive disclosure, prompt always visible:
 *   Zone 1  intent cards (optional accelerators — never gates)
 *   Zone 2  per-intent picker (teach = standards-first column; motivate/
 *           classroom/school = curated filtered lists; custom = nothing)
 *   Zone 3  grade (required; FIXED position for every intent; "All grades"
 *           allowed everywhere and OMITS gradeMin/gradeMax)
 *   Zone 4  prompt (required; ≥10 chars, relaxed to ≥4 when a chip is picked)
 *   Zone 5  style (unchanged StylePicker)
 * Typing with no card tapped = the custom path, first-class.
 * CategoryBrowser (manual tree drill-down) is deleted — the worker's scoped
 * States-free matcher auto-links the category on every submit.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWallet } from '../../../contexts/WalletContext';
import { Icon, Tag } from '../../../ui/index.jsx';
import IntentCards, { INTENTS } from './IntentCards';
import IntentListPanel from './IntentListPanel';
import TeachPanel from './TeachPanel';
import GradeBandPicker, { ALL_GRADES, bandToRange } from './GradeBandPicker';
import StylePicker from './StylePicker';

const PROMPT_PLACEHOLDER_KEYS = {
  teach: 'describe.placeholder.teach',
  motivate: 'describe.placeholder.motivate',
  classroom: 'describe.placeholder.classroom',
  school: 'describe.placeholder.school',
  custom: 'describe.placeholder.custom',
};

export default function DescribePanel({
  value, onChange, onSubmit, creditCost,
  stateCode, onStateChange, onNeedState, jurisdictions, token, restoredBand,
}) {
  const { t } = useTranslation('home');
  const { intent, subject, subjectCategory, gradeBand, prompt, styleId, standard } = value;

  const hasChip = !!(standard?.code || subjectCategory?.id);
  const promptMin = hasChip ? 4 : 10;
  const canSubmit = !!gradeBand && prompt && prompt.trim().length >= promptMin;

  const setIntent = (next) => {
    // Intent switch clears dependent picks (spec §3 invalidation rule).
    onChange({ ...value, intent: next, subject: null, subjectCategory: null, standard: null });
  };

  const setBand = (band) => {
    // Band change re-validates the picked standard: clear it when its grade
    // range no longer overlaps the new band (spec §3 invalidation rule).
    let nextStandard = standard;
    if (standard && band && band !== ALL_GRADES) {
      const { gradeMin, gradeMax } = bandToRange(band);
      const sMin = standard.gradeMin ?? null;
      const sMax = standard.gradeMax ?? null;
      if (sMin != null && sMax != null && (sMax < gradeMin || sMin > gradeMax)) nextStandard = null;
    }
    onChange({ ...value, gradeBand: band, standard: nextStandard });
  };

  const setSubject = (next) => {
    // Subject change clears the picked standard (different search space).
    onChange({ ...value, subject: next, standard: next === subject ? standard : null });
  };

  const range = bandToRange(gradeBand);
  // Brightivity (handoff 02-AI-Poster-Maker.png): numbered sections on the left, the navy "Your poster" panel on the
  // right with what was picked, the Generate button and the credit line.
  const { balance } = useWallet();
  const [styleList, setStyleList] = useState([]);
  const intentDef = INTENTS.find((x) => x.key === intent);
  const styleName = styleId ? (styleList.find((x) => x.id === styleId) || {}).displayName : null;
  const gradeLabel = !gradeBand ? null : gradeBand === ALL_GRADES ? t('gradeBand.allGrades') : `Grades ${gradeBand.replace('-', '–')}`;
  const count = (prompt || '').length;

  return (
    <div className="bv-aigen" data-testid="ai-form">
      <div className="bv-aigen__form">
        <section className="bv-aigen__sec">
          <h3 className="bv-aigen__num">1 · {t('intentCards.heading')}</h3>
          <IntentCards value={intent} onChange={setIntent} hideHeader />
          {/* per-intent picker (custom / none: nothing renders) */}
          {intent === 'teach' && (
            <TeachPanel
              subject={subject}
              onSubjectChange={setSubject}
              standard={standard}
              onStandardChange={(st) => onChange({ ...value, standard: st })}
              stateCode={stateCode}
              onStateChange={onStateChange}
              onNeedState={onNeedState}
              jurisdictions={jurisdictions}
              gradeBand={gradeBand}
              token={token}
            />
          )}
          {(intent === 'motivate' || intent === 'classroom' || intent === 'school') && (
            <IntentListPanel intent={intent} value={subjectCategory} onChange={(cat) => onChange({ ...value, subjectCategory: cat })} />
          )}
          {intent && intent !== 'custom' && !standard?.code && !subjectCategory?.id && <p className="bv-small bv-muted">{t('describe.pickingOptional')}</p>}
        </section>

        <section className="bv-aigen__sec">
          <h3 className="bv-aigen__num">2 · {t('gradeBand.label')}</h3>
          <GradeBandPicker value={gradeBand} onChange={setBand} required allowAll restored={restoredBand} bare />
        </section>

        <section className="bv-aigen__sec">
          <h3 className="bv-aigen__num">3 · {t('describe.yourIdea')}</h3>
          <div className="bv-aigen__idea">
            <textarea
              rows={3}
              maxLength={500}
              aria-label={t('describe.yourIdea')}
              placeholder={t(PROMPT_PLACEHOLDER_KEYS[intent] ?? PROMPT_PLACEHOLDER_KEYS.custom)}
              value={prompt || ''}
              onChange={(e) => onChange({ ...value, prompt: e.target.value })}
              className="dpPrompt"
            />
            <span className="bv-aigen__count">{count} / 500</span>
          </div>
          {!canSubmit && prompt && prompt.trim().length > 0 && prompt.trim().length < promptMin && <p className="bv-small bv-muted">{t('describe.moreWords')}</p>}
          {(!prompt || prompt.trim().length === 0) && (
            <div className="bv-aigen__try">
              <span>{t('describe.examplesLabel')}</span>
              {[0, 1, 2].map((i) => {
                const key = `describe.examples.${intent ?? 'custom'}.${i}`;
                const text = t(key);
                if (!text || text === key) return null;
                return <button key={i} type="button" className="dpExample" onClick={() => onChange({ ...value, prompt: text })}>{text}</button>;
              })}
            </div>
          )}
        </section>

        <section className="bv-aigen__sec">
          <StylePicker
            value={styleId}
            onChange={(v) => onChange({ ...value, styleId: v })}
            subject={intent === 'teach' ? subject : subjectCategory?.name}
            gradeMin={range.gradeMin}
            gradeMax={range.gradeMax}
            label={`4 · ${t('stylePicker.label')}`}
            onStylesLoaded={setStyleList}
          />
        </section>
      </div>

      <aside className="bv-aigen__aside" aria-label="Your poster">
        <div className="bv-aigen__asidehead">Your poster</div>
        <div className="bv-aigen__preview" aria-hidden="true">
          <span className="bv-aigen__bar bv-aigen__bar--blue" />
          <span className="bv-aigen__bar" />
          <span className="bv-aigen__art"><Icon name="sparkle" size={44} /></span>
          <span className="bv-aigen__bar bv-aigen__bar--long" />
          <span className="bv-aigen__bar" />
        </div>
        <div className="bv-aigen__tags" data-testid="ai-summary">
          {intentDef && <Tag color="blue">{t(intentDef.labelKey)}</Tag>}
          {gradeLabel && <Tag color="blue">{gradeLabel}</Tag>}
          <Tag color="purple">{styleName || 'Auto style'}</Tag>
          {(standard?.code || subjectCategory?.name) && <Tag color="green">{standard?.code || subjectCategory?.name}</Tag>}
        </div>
        <button onClick={onSubmit} disabled={!canSubmit} className="bv-btn bv-btn--coral bv-aigen__go dpGenerate" data-testid="ai-generate">
          {t('describe.generateButton', { count: creditCost ?? 0 })}
        </button>
        <p className="bv-aigen__note">
          {!canSubmit ? (!gradeBand ? t('describe.pickGrade') : t('describe.addIdeaHint'))
            : balance != null && creditCost ? `Uses ${creditCost} of your ${balance} ${balance === 1 ? 'credit' : 'credits'}` : ''}
        </p>
      </aside>
    </div>
  );
}

const lab = {
  fontSize: '0.72rem', fontWeight: 600, color: 'rgba(27,37,80,0.427)',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
};

const inputStyle = {
  width: '100%', padding: '13px 14px', borderRadius: 10,
  border: '1px solid rgba(27,37,80,0.095)',
  background: '#FFFFFF',
  color: 'rgba(27,37,80,0.9)', fontSize: '0.92rem',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  lineHeight: 1.5,
};

// Solid amber gradient — the primary CTA now carries the most visual weight
// on the panel (2026-07-29 UI pass; the old ghost treatment was weaker than
// the style thumbnails above it).
const primary = {
  padding: '14px 24px', borderRadius: 12, border: 'none',
  background: 'linear-gradient(135deg, #7F4BF6, #9B6DFF)',
  color: '#FFFFFF',
  boxShadow: '0 6px 24px rgba(127,75,246,0.35)',
  fontSize: '0.92rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
  minWidth: 190,
  transition: 'filter 0.15s ease',
};

const disabledPrimary = {
  ...primary,
  background: 'rgba(127,75,246,0.12)',
  color: 'rgba(127,75,246,0.5)',
  boxShadow: 'none',
  cursor: 'not-allowed',
};
